// @ts-nocheck
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { usePermissions } from '@/hooks/usePermissions';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Plus, Edit, Trash2, Eye } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

interface OrgMember {
  id: string;
  user_id: string;
  role: 'owner' | 'admin' | 'member' | 'contratado';
  active: boolean;
  joined_at: string;
  profiles: {
    nome: string;
    email: string;
  };
}

interface NewMemberForm {
  email: string;
  role: 'admin' | 'member';
}

interface OrganizationRequest {
  id: string;
  email: string;
  organization_name: string;
  reason: string;
  status: 'pending' | 'approved' | 'rejected';
  created_at: string;
}

export default function OrganizacaoMembros() {
  const { orgId } = useParams();
  const { hasPermission } = usePermissions();
  const { toast } = useToast();
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [requests, setRequests] = useState<OrganizationRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [selectedMember, setSelectedMember] = useState<OrgMember | null>(null);
  const [newRole, setNewRole] = useState<'admin' | 'member'>('member');
  const [newMemberForm, setNewMemberForm] = useState<NewMemberForm>({
    email: '',
    role: 'member'
  });

  useEffect(() => {
    if (orgId) {
      loadMembers();
      loadPendingRequests();
    }
  }, [orgId]);

  const loadMembers = async () => {
    if (!orgId) return;
    
    try {
      setLoading(true);
      // Primeiro buscar os membros da organização
      const { data: membershipsData, error: membershipsError } = await supabase
        .from('org_memberships')
        .select('id, user_id, role, active, joined_at')
        .eq('org_id', orgId)
        .eq('active', true)
        .order('joined_at', { ascending: false });

      if (membershipsError) throw membershipsError;

      if (!membershipsData || membershipsData.length === 0) {
        setMembers([]);
        return;
      }

      // Buscar os perfis dos usuários separadamente
      const userIds = membershipsData.map(m => m.user_id);
      const { data: profilesData, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, nome, email')
        .in('user_id', userIds);

      if (profilesError) {
        console.error('Erro ao buscar profiles:', profilesError);
      }

      // Para usuários sem perfil, buscar o email diretamente da função
      const membersWithProfiles = await Promise.all(
        membershipsData.map(async (membership) => {
          let profile = profilesData?.find(p => p.user_id === membership.user_id);
          
          if (!profile) {
            // Buscar email do usuário usando a função
            const { data: userEmail } = await supabase
              .rpc('get_user_email', { user_id: membership.user_id });
            
            profile = {
              user_id: membership.user_id,
              nome: 'Nome não informado',
              email: userEmail || 'E-mail não disponível'
            };
          }
          
          return {
            ...membership,
            profiles: profile
          };
        })
      );

      console.log('Membros carregados:', membersWithProfiles);
      // @ts-ignore - Temporary fix for role type mismatch
      setMembers(membersWithProfiles);
    } catch (error: any) {
      console.error('Erro ao carregar membros:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar membros da organização: " + error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadPendingRequests = async () => {
    if (!orgId) return;

    try {
      setRequestsLoading(true);
      const { data, error } = await supabase
        .from('organization_requests')
        .select('*')
        .eq('organization_id', orgId)
        .eq('status', 'pending')
        .order('created_at', { ascending: false });

      if (error) throw error;

      setRequests(data || []);
    } catch (error: any) {
      console.error('Erro ao carregar solicitações:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar solicitações pendentes: " + error.message,
        variant: "destructive"
      });
    } finally {
      setRequestsLoading(false);
    }
  };

  const handleApproveRequest = async (requestId: string, email: string) => {
    try {
      // Get current user for created_by field
      const { data: currentUser } = await supabase.auth.getUser();
      
      if (!currentUser.user) {
        toast({
          title: "Erro",
          description: "Usuário não autenticado",
          variant: "destructive"
        });
        return;
      }

      // Get the user ID by email
      const { data: userData, error: userError } = await supabase
        .rpc('get_user_id_by_email', { user_email: email });

      if (userError || !userData) {
        toast({
          title: "Erro",
          description: "Usuário não encontrado no sistema",
          variant: "destructive"
        });
        return;
      }

      // Check if user is already a member
      const { data: existingMember } = await supabase
        .from('org_memberships')
        .select('id')
        .eq('user_id', userData)
        .eq('org_id', orgId)
        .eq('active', true)
        .single();

      if (existingMember) {
        toast({
          title: "Erro",
          description: "Este usuário já é membro da organização",
          variant: "destructive"
        });
        return;
      }

      // Add user as member
      const { error: memberError } = await supabase
        .from('org_memberships')
        .insert({
          user_id: userData,
          org_id: orgId,
          role: 'member',
          invited_by: currentUser.user.id,
          created_by: currentUser.user.id,
          updated_by: currentUser.user.id
        });

      if (memberError) throw memberError;

      // Update request status
      const { error: updateError } = await supabase
        .from('organization_requests')
        .update({ 
          status: 'approved',
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (updateError) throw updateError;

      toast({
        title: "Sucesso",
        description: "Solicitação aprovada e usuário adicionado como membro"
      });

      loadMembers();
      loadPendingRequests();
    } catch (error: any) {
      console.error('Erro ao aprovar solicitação:', error);
      toast({
        title: "Erro",
        description: "Erro ao aprovar solicitação: " + error.message,
        variant: "destructive"
      });
    }
  };


  const handleRejectRequest = async (requestId: string) => {
    try {
      const { error } = await supabase
        .from('organization_requests')
        .update({ 
          status: 'rejected',
          updated_at: new Date().toISOString()
        })
        .eq('id', requestId);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Solicitação recusada"
      });

      loadPendingRequests();
    } catch (error: any) {
      console.error('Erro ao recusar solicitação:', error);
      toast({
        title: "Erro",
        description: "Erro ao recusar solicitação: " + error.message,
        variant: "destructive"
      });
    }
  };

  const handleAddMember = async () => {
    if (!orgId) {
      toast({
        title: "Erro",
        description: "ID da organização não fornecido",
        variant: "destructive"
      });
      return;
    }

    if (!newMemberForm.email.trim()) {
      toast({
        title: "Erro",
        description: "E-mail é obrigatório",
        variant: "destructive"
      });
      return;
    }

    try {
      // Get current user for created_by field
      const { data: currentUser } = await supabase.auth.getUser();
      
      if (!currentUser.user) {
        toast({
          title: "Erro",
          description: "Usuário não autenticado",
          variant: "destructive"
        });
        return;
      }

      // First, get the user ID by email
      const { data: userData, error: userError } = await supabase
        .rpc('get_user_id_by_email', { user_email: newMemberForm.email.trim() });

      if (userError) {
        console.error('Erro ao buscar usuário:', userError);
        toast({
          title: "Erro",
          description: "Erro ao buscar usuário: " + userError.message,
          variant: "destructive"
        });
        return;
      }

      if (!userData) {
        toast({
          title: "Erro",
          description: "Usuário não encontrado com este e-mail. Verifique se o usuário já se cadastrou no sistema.",
          variant: "destructive"
        });
        return;
      }

      // Check if user is already a member
      const { data: existingMember, error: checkError } = await supabase
        .from('org_memberships')
        .select('id')
        .eq('user_id', userData)
        .eq('org_id', orgId)
        .eq('active', true)
        .single();

      if (checkError && checkError.code !== 'PGRST116') { // PGRST116 = no rows found
        console.error('Erro ao verificar membro existente:', checkError);
        toast({
          title: "Erro",
          description: "Erro ao verificar membro existente",
          variant: "destructive"
        });
        return;
      }

      if (existingMember) {
        toast({
          title: "Erro",
          description: "Este usuário já é membro da organização",
          variant: "destructive"
        });
        return;
      }

      // Add new member
      const { error: insertError } = await supabase
        .from('org_memberships')
        .insert({
          user_id: userData,
          org_id: orgId,
          role: newMemberForm.role,
          invited_by: currentUser.user.id,
          created_by: currentUser.user.id,
          updated_by: currentUser.user.id
        });

      if (insertError) {
        console.error('Erro ao inserir membro:', insertError);
        toast({
          title: "Erro",
          description: "Erro ao adicionar membro: " + insertError.message,
          variant: "destructive"
        });
        return;
      }

      toast({
        title: "Sucesso",
        description: "Membro adicionado com sucesso"
      });

      setIsAddDialogOpen(false);
      setNewMemberForm({ email: '', role: 'member' });
      loadMembers();
    } catch (error: any) {
      console.error('Erro ao adicionar membro:', error);
      toast({
        title: "Erro",
        description: "Erro inesperado: " + error.message,
        variant: "destructive"
      });
    }
  };

  const handleUpdateRole = async (memberId: string, newRole: string) => {
    try {
      const { error } = await supabase
        .from('org_memberships')
        .update({ role: newRole })
        .eq('id', memberId);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Papel do membro atualizado com sucesso"
      });

      loadMembers();
    } catch (error: any) {
      console.error('Erro ao atualizar papel:', error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar papel do membro",
        variant: "destructive"
      });
    }
  };

  const handleUpdateRoleSubmit = async () => {
    if (!selectedMember) return;

    try {
      const { error } = await supabase
        .from('org_memberships')
        .update({ role: newRole })
        .eq('id', selectedMember.id);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Papel do membro atualizado com sucesso",
      });

      setShowEditDialog(false);
      setSelectedMember(null);
      loadMembers();
    } catch (error: any) {
      console.error('Erro ao atualizar papel do membro:', error);
      toast({
        title: "Erro",
        description: "Erro ao atualizar papel do membro: " + error.message,
        variant: "destructive"
      });
    }
  };

  const handleRemoveMember = async (memberId: string) => {
    try {
      const { error } = await supabase
        .from('org_memberships')
        .update({ active: false })
        .eq('id', memberId);

      if (error) throw error;

      toast({
        title: "Sucesso",
        description: "Membro removido com sucesso"
      });

      loadMembers();
    } catch (error: any) {
      console.error('Erro ao remover membro:', error);
      toast({
        title: "Erro",
        description: "Erro ao remover membro",
        variant: "destructive"
      });
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'owner': return 'Proprietário';
      case 'admin': return 'Administrador';
      case 'member': return 'Membro';
      case 'contratado': return 'Contratado';
      default: return role;
    }
  };

  const getRoleBadgeColor = (role: string) => {
    switch (role) {
      case 'owner': return 'bg-primary text-primary-foreground';
      case 'admin': return 'bg-destructive text-destructive-foreground';
      case 'member': return 'bg-secondary text-secondary-foreground';
      case 'contratado': return 'bg-orange-500 text-white';
      default: return 'bg-muted text-muted-foreground';
    }
  };

  if (!orgId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Erro</CardTitle>
          <CardDescription>
            ID da organização não fornecido.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold">Membros da Organização</h1>
          <p className="text-muted-foreground">
            Gerencie os membros e seus papéis na organização
          </p>
        </div>
        
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Membro
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Adicionar Novo Membro</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label htmlFor="email">E-mail</Label>
                <Input
                  id="email"
                  type="email"
                  value={newMemberForm.email}
                  onChange={(e) => setNewMemberForm({ ...newMemberForm, email: e.target.value })}
                  placeholder="usuario@exemplo.com"
                />
              </div>
              <div>
                <Label htmlFor="role">Papel</Label>
                <Select value={newMemberForm.role} onValueChange={(value: any) => setNewMemberForm({ ...newMemberForm, role: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="member">Membro</SelectItem>
                    <SelectItem value="admin">Administrador</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleAddMember} className="w-full">
                Adicionar Membro
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Solicitações Pendentes */}
      {requests.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Solicitações Pendentes</CardTitle>
            <CardDescription>
              Usuários aguardando aprovação para se juntar à organização
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>E-mail</TableHead>
                  <TableHead>Motivo</TableHead>
                  <TableHead>Data da Solicitação</TableHead>
                  <TableHead>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {requestsLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center">
                      Carregando solicitações...
                    </TableCell>
                  </TableRow>
                ) : requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center">
                      Nenhuma solicitação pendente
                    </TableCell>
                  </TableRow>
                ) : (
                  requests.map((request) => (
                    <TableRow key={request.id}>
                      <TableCell className="font-medium">
                        {request.email}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {request.reason}
                      </TableCell>
                      <TableCell>
                        {new Date(request.created_at).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleApproveRequest(request.id, request.email)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            Aprovar
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRejectRequest(request.id)}
                          >
                            Recusar
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Membros Atuais */}
      <Card>
        <CardHeader>
          <CardTitle>Membros Atuais</CardTitle>
          <CardDescription>
            Lista de todos os membros da organização
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>E-mail</TableHead>
                <TableHead>Papel</TableHead>
                <TableHead>Data de Entrada</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : members.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="text-center">
                  Nenhum membro encontrado
                </TableCell>
              </TableRow>
            ) : (
              members.map((member) => (
                <TableRow key={member.id}>
                  <TableCell className="font-medium">
                    {member.profiles?.nome || 'Nome não disponível'}
                  </TableCell>
                  <TableCell>{member.profiles?.email}</TableCell>
                  <TableCell>
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRoleBadgeColor(member.role)}`}>
                      {getRoleLabel(member.role)}
                    </span>
                  </TableCell>
                  <TableCell>
                    {new Date(member.joined_at).toLocaleDateString('pt-BR')}
                  </TableCell>
                  <TableCell>
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedMember(member);
                          setShowViewDialog(true);
                        }}
                        title="Visualizar membro"
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      {member.role !== 'owner' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedMember(member);
                              setNewRole(member.role);
                              setShowEditDialog(true);
                            }}
                            title="Editar papel do membro"
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRemoveMember(member.id)}
                            title="Remover membro"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog para visualizar membro */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Detalhes do Membro</DialogTitle>
          </DialogHeader>
          {selectedMember && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Nome</Label>
                <p className="text-sm text-muted-foreground">
                  {selectedMember.profiles?.nome || 'Nome não disponível'}
                </p>
              </div>
              <div>
                <Label className="text-sm font-medium">E-mail</Label>
                <p className="text-sm text-muted-foreground">
                  {selectedMember.profiles?.email || 'E-mail não disponível'}
                </p>
              </div>
              <div>
                <Label className="text-sm font-medium">Papel na Organização</Label>
                <p className="text-sm text-muted-foreground">
                  {getRoleLabel(selectedMember.role)}
                </p>
              </div>
              <div>
                <Label className="text-sm font-medium">Data de Ingresso</Label>
                <p className="text-sm text-muted-foreground">
                  {new Date(selectedMember.joined_at).toLocaleDateString('pt-BR')}
                </p>
              </div>
              <div>
                <Label className="text-sm font-medium">Status</Label>
                <p className="text-sm text-muted-foreground">
                  {selectedMember.active ? 'Ativo' : 'Inativo'}
                </p>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Dialog para editar papel do membro */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar Papel do Membro</DialogTitle>
          </DialogHeader>
          {selectedMember && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm font-medium">Membro</Label>
                <p className="text-sm text-muted-foreground">
                  {selectedMember.profiles?.nome || 'Nome não disponível'} 
                  ({selectedMember.profiles?.email})
                </p>
              </div>
              <div>
                <Label htmlFor="role-select">Novo Papel</Label>
                <Select value={newRole} onValueChange={(value: 'admin' | 'member') => setNewRole(value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o papel" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Administrador</SelectItem>
                    <SelectItem value="member">Membro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEditDialog(false);
                    setSelectedMember(null);
                  }}
                >
                  Cancelar
                </Button>
                <Button onClick={handleUpdateRoleSubmit}>
                  Salvar
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}